import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { InkButton } from '../components/InkButton';
import { InkedTitle } from '../components/InkedTitle';
import { Reveal } from '../components/Reveal';
import { WorkCard } from '../components/WorkCard';
import { useBooking } from '../context/BookingContext';
import { artists, studio, type Work, works } from '../data';
import { useLang } from '../hooks/useLang';
import { useMagnetic } from '../hooks/useMagnetic';

// 5 lavori selezionati per coerenza visiva: tutti close-up arto, treatment simile
const featuredWorkIds = ['w-001', 'w-005', 'w-013', 'w-006', 'w-014'];

export function HomePage() {
  const { t } = useTranslation();
  const lang = useLang();
  const { openBooking } = useBooking();
  const heroTitleRef = useMagnetic<HTMLHeadingElement>({ radius: 280, strength: 0.14 });

  const featuredWorks = featuredWorkIds
    .map((id) => works.find((w) => w.id === id))
    .filter((w): w is Work => Boolean(w));

  return (
    <>
      {/* HERO — tipografia bleed + portrait media. Mobile-first: il titolo non sta al fondo */}
      <section className="relative min-h-[100svh] overflow-hidden bg-ink-base flex flex-col">
        <div className="relative mx-auto w-full max-w-[1800px] px-6 pt-24 md:px-10 md:pt-28 flex-1 flex flex-col">
          {/* EYEBROW mono — single rosso "bullet" del viewport */}
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
            className="font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-ink-muted flex items-center gap-3"
          >
            <span className="inline-block size-1.5 rounded-full bg-ink-accent" aria-hidden />
            <span>00 — {lang === 'it' ? 'Milano · Studio · Dal 2017' : 'Milan · Studio · Est. 2017'}</span>
          </motion.p>

          {/* TITLE ROW — title fills width; on desktop the video tucks into the right of the title (column 8-12) */}
          <div className="relative grid grid-cols-12 gap-x-6 gap-y-8 md:gap-x-10 mt-10 md:mt-auto">
            <div className="col-span-12 md:col-span-8">
              <InkedTitle
                ref={heroTitleRef}
                as="h1"
                immediate
                delay={0.1}
                duration={1.1}
                className="font-display tracking-display leading-[0.82] text-[clamp(3.25rem,10.5vw,11rem)] lowercase"
              >
                {t('hero.title')}
              </InkedTitle>
            </div>

            {/* Media column — visible on every viewport. Desktop slots beside the headline, mobile becomes a second beat under the title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
              className="col-span-12 md:col-span-4 flex flex-col gap-3 md:self-end"
            >
              <div className="relative aspect-[16/11] md:aspect-[3/4] overflow-hidden bg-ink-line">
                <img
                  src="https://images.unsplash.com/photo-1610942933193-8fafd0973f6d?w=1200&q=85&auto=format&fit=crop"
                  alt="Magnolia notturna — Davi Marçal"
                  aria-hidden
                  fetchPriority="high"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover ink-photo"
                />
                <video
                  src="/hero-tattoo.mp4"
                  poster="https://images.unsplash.com/photo-1610942933193-8fafd0973f6d?w=1200&q=85&auto=format&fit=crop"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 h-full w-full object-cover ink-photo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-base/40 via-transparent to-transparent" />
              </div>
              <div className="flex items-baseline justify-between gap-3 text-[12px] md:text-[13px] text-ink-paper/65 font-display tracking-display lowercase">
                <span>Magnolia notturna — Davi Marçal</span>
                <span className="text-ink-muted">seduta 02/03, 2025</span>
              </div>
            </motion.div>
          </div>

          {/* Subtitle + CTAs ROW — under everything, near bottom */}
          <div className="mt-10 md:mt-12 mb-10 md:mb-14 grid gap-10 md:grid-cols-12 items-end">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.7 }}
              className="md:col-span-7 max-w-2xl text-lg md:text-xl text-ink-paper leading-[1.3]"
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="md:col-span-5 md:justify-self-end flex flex-wrap items-center gap-x-10 gap-y-5"
            >
              <InkButton as="button" size="lg" onClick={() => openBooking()}>
                {t('hero.cta')}
              </InkButton>
              <InkButton as="link" to="/lavori" size="md" variant="link">
                {t('hero.secondary')}
              </InkButton>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURED WORK — pattern A: solo titolo gigante, no eyebrow, no lead */}
      <section className="relative border-t border-ink-line/40 px-6 py-24 md:px-10 md:py-40">
        <div className="mx-auto max-w-[1800px]">
          <Reveal>
            <p className="font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-ink-muted mb-10 md:mb-14">
              01 — {lang === 'it' ? 'Archivio · 2024 — 2025' : 'Archive · 2024 — 2025'}
            </p>
            <div className="flex items-end justify-between gap-8 flex-wrap">
              <InkedTitle
                as="h2"
                className="font-display tracking-display leading-[0.86] text-[clamp(3rem,11vw,11rem)] lowercase"
              >
                {t('sections.selectedWork')}
              </InkedTitle>
              <Link
                to="/lavori"
                className="group inline-flex items-baseline gap-3 text-lg font-display lowercase tracking-display text-ink-muted hover:text-ink-paper transition-colors pb-4"
              >
                <span>
                  {works.length} {lang === 'it' ? 'archiviati' : 'archived'}
                </span>
                <span aria-hidden className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </Link>
            </div>
          </Reveal>

          <div className="mt-16 md:mt-24 grid gap-x-6 gap-y-10 md:gap-x-10 md:gap-y-20 grid-cols-2 md:grid-cols-12">
            {/* Editorial spread asimmetrico — il dominante sfora a sinistra fino al bleed */}
            <Reveal className="md:col-span-7 md:row-span-2 md:-ml-10" delay={0.0}>
              <WorkCard work={featuredWorks[0]!} index={0} size="lg" />
            </Reveal>
            <Reveal className="md:col-span-4 md:col-start-9" delay={0.08}>
              <WorkCard work={featuredWorks[1]!} index={1} />
            </Reveal>
            <Reveal className="md:col-span-5 md:col-start-8 md:mt-12" delay={0.16}>
              <WorkCard work={featuredWorks[2]!} index={2} />
            </Reveal>
            <Reveal className="md:col-span-3 md:col-start-3" delay={0.0}>
              <WorkCard work={featuredWorks[3]!} index={3} />
            </Reveal>
            <Reveal className="md:col-span-5 md:col-start-7 md:-mt-32" delay={0.08}>
              <WorkCard work={featuredWorks[4]!} index={4} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ARTISTS — pattern B: titolo a destra, no eyebrow, no numerazione, band edge-to-edge */}
      <section className="bg-ink-line/30 border-y border-ink-line/40 overflow-hidden">
        <div className="px-6 pt-24 md:px-10 md:pt-36">
          <div className="mx-auto max-w-[1800px] grid gap-8 md:grid-cols-12 items-end">
            <Reveal className="md:col-span-7 md:col-start-1">
              <p className="font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-ink-muted">
                02 — {lang === 'it' ? 'Quattro residenti' : 'Four residents'}
              </p>
              <InkedTitle
                as="h2"
                className="mt-6 font-display tracking-display leading-[0.86] text-[clamp(3rem,10vw,9rem)] lowercase"
              >
                {t('artist.indexTitle')}
              </InkedTitle>
            </Reveal>
            <Reveal className="md:col-span-4 md:col-start-9 md:justify-self-end" delay={0.1}>
              <p className="max-w-md text-base md:text-lg text-ink-paper/75">
                {t('sections.artistsLead')}
              </p>
              <div className="mt-8">
                <InkButton as="link" to="/artisti" size="md" variant="link">
                  {t('common.viewMore').toLowerCase()}
                </InkButton>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-line/40">
          {artists.map((a, i) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to={`/artisti/${a.slug}`}
                className="group relative block aspect-[3/4] md:aspect-[2/3] overflow-hidden bg-ink-base"
              >
                <img
                  src={a.portrait}
                  alt={a.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover ink-photo group-hover:ink-photo-hover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-base via-ink-base/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3 md:p-6">
                  <p className="text-sm font-display lowercase text-ink-paper/65 tracking-tight">
                    @{a.alias}
                  </p>
                  <p className="mt-2 font-display tracking-display leading-none text-2xl md:text-4xl lg:text-5xl lowercase">
                    {a.name.split(' ')[0]}
                    <br className="hidden md:inline" />
                    <span className="md:block">{a.name.split(' ').slice(1).join(' ')}</span>
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MANIFESTO — pattern C: nessun header pattern, solo testo editoriale grande */}
      <section className="relative px-6 py-32 md:px-10 md:py-52 overflow-hidden">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-ink-muted mb-12">
              03 — {studio.address.city} · {t('studio.headerEyebrow')}
            </p>
            <p className="font-display tracking-display leading-[1.04] text-[clamp(1.75rem,4.2vw,4rem)] text-ink-paper">
              {studio.ethos[lang].split('. ').map((sentence, i, arr) => {
                const isHighlight = i === 1;
                const highlightWord = lang === 'it' ? 'peso' : 'weight';
                const isLast = i === arr.length - 1;
                if (isHighlight) {
                  const parts = sentence.split(highlightWord);
                  return (
                    <span key={i} className="text-ink-paper">
                      {parts.map((part, idx) => (
                        <span key={idx}>
                          {part}
                          {idx < parts.length - 1 && (
                            <span className="text-ink-accent">{highlightWord}</span>
                          )}
                        </span>
                      ))}
                      .{' '}
                    </span>
                  );
                }
                return (
                  <span key={i} className="text-ink-paper/55">
                    {sentence}
                    {!isLast ? '. ' : ''}
                  </span>
                );
              })}
            </p>
            <div className="mt-16 flex flex-wrap items-center gap-x-10 gap-y-5">
              <InkButton as="link" to="/studio" size="md" variant="link">
                {t('studio.process').toLowerCase()}
              </InkButton>
              <InkButton
                as="a"
                size="md"
                variant="link"
                suffix="↗"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${studio.address.street}, ${studio.address.zip} ${studio.address.city}`,
                )}`}
                target="_blank"
                rel="noreferrer"
              >
                {studio.address.street.toLowerCase()}
              </InkButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA — niente lead, niente eyebrow, una sola frase enorme + bottone */}
      <section className="relative bg-ink-paper text-ink-base px-6 py-32 md:px-10 md:py-52 overflow-hidden">
        <div className="mx-auto max-w-[1800px]">
          <Reveal>
            <p className="font-mono text-[11px] md:text-xs tracking-[0.24em] uppercase text-ink-muted mb-10 md:mb-14">
              04 — {lang === 'it' ? 'Prenota' : 'Book'}
            </p>
            <InkedTitle
              as="h2"
              className="font-display tracking-display leading-[0.88] text-[clamp(3rem,12vw,13rem)] lowercase text-ink-base"
            >
              {lang === 'it' ? 'Prendi appuntamento' : 'Book a session'}
              <span className="text-ink-accent">.</span>
            </InkedTitle>
            <div className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
              <p className="max-w-xl text-base md:text-lg text-ink-base/70">
                {t('sections.bookLead')}
              </p>
              <InkButton
                as="button"
                tone="dark"
                size="lg"
                onClick={() => openBooking()}
              >
                {t('hero.cta')}
              </InkButton>
            </div>
          </Reveal>
        </div>
      </section>

    </>
  );
}
